import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import type { Booking } from "@/types";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowLeft } from "lucide-react";

export default function PastServices() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/filmmaker/login');
      return;
    }

    const loadBookings = async () => {
      try {
        setLoading(true);

        const { data, error } = await supabase
            .from('bookings')
            .select(`
              *,
              package:packages(*),
              customer:profiles!bookings_customer_id_fkey(
                id,
                full_name,
                phone,
                email
              )
            `)
            .eq('filmmaker_id', user.id)
            .in('status', ['uploaded', 'completed'])
            .order('scheduled_at', { ascending: false });

        if (error) {
          console.error('Error loading bookings:', error);
        } else {
          setBookings(data || []);
        }
      } catch (error) {
        console.error('Error loading bookings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBookings();
  }, [user, navigate]);

  const userDisplayName = user?.fullName || user?.phone || 'User';
  const userAvatarUrl = user?.avatar_url;

  return (
    <div className="min-h-screen bg-background pb-8">
      <header className="sticky top-0 z-10 bg-white px-4 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/filmmaker')}
              className="h-10 w-10"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold">Your Past Shootings</h1>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-10 w-10 rounded-full p-0">
                {userAvatarUrl ? (
                  <Avatar className="h-10 w-10">
                    <AvatarFallback>
                      {userDisplayName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-gray-100 text-gray-600">
                      {userDisplayName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={signOut} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="px-4 pt-6 max-w-screen-md mx-auto">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading past services...</p>
          </div>
        ) : bookings.length === 0 ? (
          <Card className="p-8 text-center">
            <h3 className="font-semibold text-lg mb-2">No past services</h3>
            <p className="text-sm text-muted-foreground">
              You haven't completed any services yet.
            </p>
          </Card>
        ) : (
          <div className="space-y-2">
            {bookings.map((booking) => (
              <div key={booking.id}>
                <Link to={`/filmmaker/jobs/${booking.id}`}>
                  <Card className="p-0 hover:border-primary transition-colors flex">
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src="https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=400&h=400&fit=crop" 
                        alt={booking.package?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 p-4">
                      <h3 className="font-bold text-base mb-1">{booking.package?.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(booking.scheduled_at), 'MMM d, yyyy')} • ${(booking.total_cents / 100).toFixed(2)}
                      </p>
                    </div>
                  </Card>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

